/*----------------------------------
- ROUTE: /product/watchlists
----------------------------------*/

/**
 * @docs docs/features/website-product-feature-pages
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import { getProductFeaturePage, productFeaturePageMirrorPath, productFeaturePagePath } from '@/common/catalogs/landing/productPages';
import { PRODUCT_WATCHLISTS_PAGE_CONTENT } from '@/client/catalogs/landing/product/watchlists/pageContent';
import { buildProductWatchlistsGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { buildProductFeatureStructuredData, toStructuredDataFaqs, type WebsiteJsonLdNode } from '@/client/pages/landing/seo/structuredData';
import ProductWatchlistsPage from './page/ProductWatchlistsPage';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';

/*----------------------------------
- ROUTES
----------------------------------*/

export default definePageRoute({
    path: '/product/watchlists',
    options: {
        auth: false,
        layout: false,
        static: {
            refresh: '1 day',
            urls: ['/product/watchlists'],
        },
    },
    data: ({ Product }) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    }),
    render: ({
        page,
        Router,
        navigation,
    }: {
        page: {
            description?: string;
            head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
            jsonld: WebsiteJsonLdNode[];
            title?: string;
            url: string;
        };
        Router: { url: (path: string) => string };
        navigation: LandingHeaderDomainsNavigation;
    }) => {
        const entry = getProductFeaturePage('watchlists');
        const canonicalUrl = Router.url(productFeaturePagePath('watchlists'));

        if (entry) {
            page.title = entry.pageTitle;
            page.description = entry.metaDescription;
        }
        page.url = canonicalUrl;
        page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
        page.head.push({
            $: 'link',
            rel: 'alternate',
            type: 'text/markdown',
            href: Router.url(productFeaturePageMirrorPath('watchlists')),
        });
        page.jsonld.push(
            ...buildProductFeatureStructuredData({
                canonicalUrl,
                description: entry?.metaDescription || '',
                faqs: toStructuredDataFaqs(PRODUCT_WATCHLISTS_PAGE_CONTENT.faq),
                featureLabel: entry?.label || 'Watchlists',
                geoContext: buildProductWatchlistsGeoContext(),
                title: entry?.pageTitle || 'Watchlists',
            }),
        );

        return <ProductWatchlistsPage navigation={navigation} />;
    },
});
